import xss from "xss";

import { Auftragsposition, Auftrag } from "rage_api";
import { isValidDate } from "../utils/functions/date";

export default class AuftragStorage {
  constructor() {
    this.auftragsListe = [];
    this.auftragsListeExtended = [];
    this.auftrag = null;
    this.errorStatus = null;
    this._loading = undefined;
    this._action = undefined;
    this._reloadStatus = undefined;
    this._auftragLoaded = false;
    this.weiterKz = false;
    this.messages = [];
    this.query = {};
  }

  setListe({ liste, weiterKz, query }) {
    if (!Array.isArray(liste)) {
      this.auftragsListe = [];
      this.weiterKz = false;
      this.query = {};
      return;
    }
    this.auftragsListe = liste.map(a => {
      // validate strings and date
      if (!isValidDate(a.datum)) {
        console.warn("invalid datum in Auftrag");
        return null;
      }
      return Auftragsposition.constructFromObject({
        ...a,
        geschaeftsart: xss(a.geschaeftsart).trim(),
        datum: a.datum,
        kennziffer: xss(a.kennziffer).trim(),
        text: xss(a.text).trim(),
        iban: xss(a.iban).trim(),
        mandatRef: xss(a.mandatRef).trim(),
        bearbeiter:{
          name: a.bearbeiter ? xss(a.bearbeiter.name).trim() : "",
          telefon: a.bearbeiter ? xss(a.bearbeiter.telefon).trim() : "",
          platz: a.bearbeiter ? xss(a.bearbeiter.platz).trim() : "",
        }
      });
    }).filter(a => !!a);
    this.weiterKz = !!String(weiterKz).trim().match(/^(J|1|true)$/i);
    this.query = query;
  }

  setListeExtended({ listeExtended, weiterKz, query }) {
    if (!Array.isArray(listeExtended)) {
      this.auftragsListeExtended = [];
      this.weiterKz = false;
      this.query = {};
      return;
    }
    this.auftragsListeExtended = listeExtended.map(a => {
      // validate strings and date
      if (!isValidDate(a.datum)) {
        console.warn("invalid datum in Auftrag");
        return null;
      }
      return Auftragsposition.constructFromObject({
        ...a,
        geschaeftsart: xss(a.geschaeftsart).trim(),
        datum: a.datum,
        kennziffer: xss(a.kennziffer).trim(),
        text: xss(a.text).trim(),
        iban: xss(a.iban).trim(),
        mandatRef: xss(a.mandatRef).trim(),
        bearbeiter:{
          name: a.bearbeiter ? xss(a.bearbeiter.name).trim() : "",
          telefon: a.bearbeiter ? xss(a.bearbeiter.telefon).trim() : "",
          platz: a.bearbeiter ? xss(a.bearbeiter.platz).trim() : "",
        }
      });
    }).filter(a => !!a);
    this.weiterKz = !!String(weiterKz).trim().match(/^(J|1|true)$/i);
    this.query = query;
  }

  getListe() {
    return this.auftragsListe;
  }

  getListeExtended() {
    return this.auftragsListeExtended;
  }

  setAuftrag(a) {
    if (a === null) this.errorStatus = null;
    this._action = null;
    if (!a || !isValidDate(a.datum)) {
      this.auftrag = null;
      this._auftragLoaded = false;
      return;
    }
    if (a instanceof Auftrag) {
      this.auftrag = a;
      this._auftragLoaded = !!this.auftrag;
      this.auftragsListe = this.auftragsListe.map(old => {
        if (old.auftragsNummer !== a.auftragsNummer) {
          this.messages[old.auftragsNummer] = {};
          return old;
        }
        const n = { ...a };
        Object.keys(n).forEach(key => {
          if (n[key] === undefined || n[key] === "") delete n[key];
        });
        this.auftrag = Auftrag.constructFromObject({ ...old, ...n });
        return Auftragsposition.constructFromObject({ ...old, ...n });
      });
      if (this._reloadStatus && this._reloadStatus.auftragsNummer === this.auftrag.auftragsNummer) {
        this._action = this._reloadStatus;
      }
      this.reload = undefined;
      return;
    }
    this.auftrag = Auftrag.constructFromObject(a);
    this._auftragLoaded = !!this.auftrag;
  }

  getAuftrag() {
    return this.auftrag;
  }

  loading(status) {
    this._loading = !!status;
  }

  isLoading() {
    return this._loading;
  }

  hasMoreData() {
    return this.weiterKz;
  }

  getSearchParams() {
    return this.query;
  }

  getActionStatus() {
    return this._action;
  }

  _setStatus(payload, auftrag) {
    if (!payload || !payload.aktion) return;
    let auftragsNummer = payload.auftragsNummer || "undefined";
    this._action = payload.aktion;
    if (this._action === "onlineStorno") {
      if (!payload.message) {
        this.messages[auftragsNummer] = { message: "Online Storno wurde erfolgreich ausgeführt", variant: "success" };
      } else {
        this.messages[auftragsNummer] = { message: payload.message, variant: payload.variant || "info" };
      }
      this._reloadStatus = payload;
    } else if (payload.aktion === "reprint" && auftragsNummer) {
      if (!payload.message) {
        this.messages[auftragsNummer] = { message: "Zweitdruck wurde erfolgreich ausgeführt", variant: "success" };
      }
      this._reloadStatus = null;
    }
  }

  setActionStatus(payload) {
    if (payload === null) {
      this.errorStatus = null;
      this._auftragLoaded = false;
      this._action = null;
      return;
    }
    let auftrag = null;
    let auftragsNummer = payload.auftragsNummer || "undefined";
    auftrag = this.auftragsListe.find((a) => a.auftragsNummer === auftragsNummer);
    this.messages[auftragsNummer] = payload.message ? { message: payload.message, variant: payload.variant || "info" } : {};
    if (payload.pending) {
      this.errorStatus = null;
      this._auftragLoaded = false;
      this._action = null;
    } else if (payload.aktion) {
      this._setStatus(payload, auftrag);
      if (payload.aktion === "reprint" && auftragsNummer) {
        const orderId = xss(payload.orderId) || null;
        if (this.auftrag && this.auftrag.auftragsNummer === auftragsNummer) {
          this.auftrag.orderId = orderId;
          this.auftrag.pdf = payload.pdf || null;
        }
        if (auftrag) {
          auftrag.action = true;
          auftrag.orderId = orderId;
          if (payload.pdf) {
            auftrag.pdf = payload.pdf;
          }
        }
      }
    } else {
      this._action = payload;
    }
  }

  getPdf() {
    if (!this.auftrag || !this.auftrag.pdf) return "";
    return this.auftrag.pdf;
  }

  isNewAuftrag() {
    return this._auftragLoaded && !this._reloadStatus;
  }

  actionSuccess() {
    return typeof this._action === "string" ? this._action : "";
  }

  actionError() {
    return this._action ? this._action.message : "";
  }

  isActionPending() {
    return this._action === null;
  }

  hasData() {
    return typeof this._loading === "boolean";
  }

  failed(error) {
    this.errorStatus = error;
  }

  getError() {
    return this.errorStatus ? this.errorStatus.message : "";
  }

  getMessage(auftragsNummer) {
    return this.messages[auftragsNummer] || {};
  }
}
